import { ISchedule } from "@gettactic/api";
import { useSettings } from "@gettactic/hooks";
import React, { useRef, useState, useCallback } from "react";
import { formatTime, getEventHeight, getEventPosition } from "./helpers";
import { CalendarEvent as ICalendarEvent } from "./types";

export type CalendarEventPropsExpanded = ICalendarEvent & {
  allowCheckIn: boolean;
  allowCheckOut: boolean;
  canEdit: boolean;
  canRespondCancel: boolean;
  bufferBefore?: number;
  bufferAfter?: number;
  isBufferBlock?: boolean;
};

interface CalendarEventProps {
  event: CalendarEventPropsExpanded;
  width: number;
  left: number;

  isDraggable?: boolean;
  isResizable?: boolean;

  externalInteracting?: boolean;

  containerRef: React.RefObject<HTMLDivElement>;
  yToDate: (y: number) => Date;

  onMove: (id: string, newStart: Date, newEnd: Date) => void;
  onResize: (
    id: string,
    edge: "start" | "end",
    newStart: Date,
    newEnd: Date
  ) => void;

  onCommit: (id: string, finalStart: Date, finalEnd: Date) => void;
  handleEditModal?: (schedule: ISchedule) => void;
  handleCancelModal?: (schedules: ISchedule[]) => void;
}

const classes = {
  wrapper: {
    base: "absolute cursor-default rounded-md px-2 py-1 shadow-sm hover:shadow-md",
    overflow: "overflow-hidden",
    overflowVisible: "overflow-visible",
    dragging:
      "ring-2 ring-offset-1 ring-blue-500/60 cursor-grabbing select-none",
    resizable: "group",
    transitionOn: "transition-all duration-200",
    transitionOff: "transition-none"
  },
  content: {
    title: "truncate font-semibold text-sm",
    time: "text-gray-600 text-xs",
    location: "truncate text-gray-600 text-xs",
    expanded: "text-xs leading-tight"
  },
  handles: {
    base: "absolute left-0 right-0 h-3 opacity-0 group-hover:opacity-100 transition-opacity z-10 touch-none",
    top: "top-0 cursor-n-resize",
    bottom: "bottom-0 cursor-s-resize",
    grip: "pointer-events-auto"
  }
};

const PIXELS_PER_MINUTE = 1;

export const CalendarEvent: React.FC<CalendarEventProps> = ({
  event,
  width,
  left,
  isDraggable = false,
  isResizable = false,
  externalInteracting = false,
  containerRef,
  yToDate,
  onMove,
  onResize,
  onCommit,
  handleEditModal,
  handleCancelModal
}) => {
  const { isOrgPrivacyEnabled } = useSettings();
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState<
    false | "move" | "start" | "end"
  >(false);

  const dragStartRef = useRef<{
    type: "move" | "start" | "end";
    y: number;
    start: Date;
    end: Date;
  } | null>(null);

  const liveFinalRef = useRef<{ start: Date; end: Date } | null>(null);

  const top = getEventPosition(event.startTime);
  const height = getEventHeight(event.startTime, event.endTime);
  const startTime = formatTime(new Date(event.startTime));
  const endTime = formatTime(new Date(event.endTime));
  // const debuggingDate = format(new Date(event.startTime), "yyyy-MM-dd");
  const tooltipContent = event.isBufferBlock
    ? `Buffer time: ${startTime} - ${endTime}`
    : `${event.title}\n${startTime} - ${endTime}\n${event.location}`;

  const isBufferBlock = event.isBufferBlock;
  // Show text in buffer blocks if hovered OR if there's enough height (at least 18px for single line)
  const showBufferText = isBufferBlock && (isHovered || height >= 18);

  const positioningStyles: React.CSSProperties = {
    top: `${top}px`,
    height: isHovered ? "auto" : `${height}px`,
    width: isHovered ? "100%" : `${width}%`,
    left: isHovered ? "0%" : `${left}%`,
    minHeight: `${height}px`,
    zIndex: isHovered
      ? (50 as unknown as number)
      : ("auto" as unknown as number)
  };

  // For buffer blocks, use solid orange when hovered
  const bufferColor = isHovered ? "rgba(255,165,0,0.8)" : "rgba(255,165,0,0.4)";

  const colorStyles: React.CSSProperties = {
    backgroundColor: isBufferBlock ? bufferColor : event.color,
    color: event.textColor,
    borderLeft: isBufferBlock
      ? "4px solid rgba(255,165,0,0.9)"
      : `4px solid ${event.borderColor || event.color}`
  };

  const getRelativeY = (clientY: number) => {
    const container = containerRef.current;
    if (!container) return 0;
    const rect = container.getBoundingClientRect();
    return clientY - rect.top + container.scrollTop;
  };

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, type: "move" | "start" | "end") => {
      if (e.button !== 0) return;
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      const target = e.target as HTMLElement;
      if (target.closest?.('[data-dots-menu="true"]')) return;
      if (target.closest?.('[role="menuitem"]')) return;
      if (target.closest?.("[data-radix-popper-content-wrapper]")) return;

      const y = getRelativeY(e.clientY);
      const start = new Date(event.startTime);
      const end = new Date(event.endTime);

      dragStartRef.current = {
        type,
        y,
        start,
        end
      };
      liveFinalRef.current = { start, end };
      setIsDragging(type);
      e.preventDefault();
    },
    [event.startTime, event.endTime]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragStartRef.current) return;

      const target = e.target as HTMLElement;
      // Ignore clicks on DotsMenu trigger and dropdown
      if (target.closest?.('[data-dots-menu="true"]')) return;
      if (target.closest?.('[role="menuitem"]')) return;
      if (target.closest?.("[data-radix-popper-content-wrapper]")) return;

      const s = dragStartRef.current;
      const yNow = getRelativeY(e.clientY);
      const deltaY = yNow - s.y; // px
      const deltaMinutes = Math.round(deltaY / PIXELS_PER_MINUTE);

      if (s.type === "move") {
        const newStart = new Date(s.start.getTime() + deltaMinutes * 60000);
        const newEnd = new Date(s.end.getTime() + deltaMinutes * 60000);
        liveFinalRef.current = { start: newStart, end: newEnd };
        onMove(event.id, newStart, newEnd);
      } else if (s.type === "start") {
        const newStart = new Date(s.start.getTime() + deltaMinutes * 60000);
        liveFinalRef.current = { start: newStart, end: s.end };
        onResize(event.id, "start", newStart, s.end);
      } else if (s.type === "end") {
        const newEnd = new Date(s.end.getTime() + deltaMinutes * 60000);
        liveFinalRef.current = { start: s.start, end: newEnd };
        onResize(event.id, "end", s.start, newEnd);
      }
      e.preventDefault();
    },
    [onMove, onResize, event.id]
  );

  const endDrag = useCallback(() => {
    if (!dragStartRef.current) return;

    setIsDragging(false);

    const latest = liveFinalRef.current || {
      start: new Date(event.startTime),
      end: new Date(event.endTime)
    };
    onCommit(event.id, latest.start, latest.end);

    dragStartRef.current = null;
    liveFinalRef.current = null;
  }, [event.id, event.startTime, event.endTime, onCommit]);

  const noTransition = isDragging || externalInteracting;

  const wrapperClass = [
    classes.wrapper.base,
    isHovered ? classes.wrapper.overflowVisible : classes.wrapper.overflow,
    isResizable ? classes.wrapper.resizable : "",
    isDragging ? classes.wrapper.dragging : "",
    noTransition ? classes.wrapper.transitionOff : classes.wrapper.transitionOn
  ].join(" ");

  return (
    <div
      data-calendar-event="true"
      className={wrapperClass}
      style={{
        ...positioningStyles,
        ...colorStyles,
        transition: noTransition ? "none" : undefined,
        willChange: noTransition ? "transform, height, top" : undefined
      }}
      title={tooltipContent}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onPointerDown={(e) => {
        if (!isDraggable) return;
        const target = e.target as HTMLElement;

        // Ignore clicks on DotsMenu trigger and dropdown
        if (target.closest?.('[data-dots-menu="true"]')) return;
        if (target.closest?.('[role="menuitem"]')) return;
        if (target.closest?.("[data-radix-popper-content-wrapper]")) return;

        const onHandle = target.closest?.('[data-resize-handle="true"]');
        if (onHandle) return;
        handlePointerDown(e, "move");
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {/* Resize handles */}
      {isResizable && (
        <>
          <div
            className={`${classes.handles.base} ${classes.handles.top}`}
            data-resize-handle="true"
            onPointerDown={(e) => {
              e.stopPropagation();
              handlePointerDown(e, "start");
            }}
          >
            <div
              className={`${classes.handles.grip} h-3 w-full`}
              data-resize-handle="true"
            />
          </div>
          <div
            className={`${classes.handles.base} ${classes.handles.bottom}`}
            data-resize-handle="true"
            onPointerDown={(e) => {
              e.stopPropagation();
              handlePointerDown(e, "end");
            }}
          >
            <div
              className={`${classes.handles.grip} h-3 w-full`}
              data-resize-handle="true"
            />
          </div>
        </>
      )}

      {isBufferBlock ? (
        // Buffer block: single line "Buffer TIME" format
        showBufferText && (
          <div
            className={`font-medium text-xs ${isHovered ? "whitespace-nowrap" : "truncate"}`}
          >
            Buffer {startTime} - {endTime}
          </div>
        )
      ) : (
        // Regular event rendering
        <>
          <div className="relative flex w-full items-center justify-between gap-2">
            <div
              className={classes.content.title}
              style={{ color: event.textColor }}
            >
              {isOrgPrivacyEnabled ? "Private" : event.title}
            </div>
          </div>
          <div
            className={classes.content.time}
            style={{ color: event.textColor }}
          >
            {startTime} - {endTime}
          </div>
          {isHovered ? (
            <div
              className={classes.content.expanded}
              style={{ color: event.textColor }}
            >
              {event.location}
            </div>
          ) : (
            <div
              className={classes.content.location}
              style={{ color: event.textColor }}
            >
              {event.location}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// const CalendarEventActionMenu = ({
//   event,
//   handleEditModal,
//   handleCancelModal
// }: {
//   event: CalendarEventPropsExpanded;
//   handleEditModal?: (schedule: ISchedule) => void;
//   handleCancelModal?: (schedules: ISchedule[]) => void;
// }) => {
//   const onRemove = () => {
//     handleCancelModal([event.id]);
//   };

//   const onManage = () => {
//     handleEditModal(event.id);
//   };

//   const getMenuEvent = (fn: () => void) => {
//     return (e: React.MouseEvent<HTMLElement>) => {
//       e.stopPropagation();
//       e.preventDefault();
//       fn();
//     };
//   };

//   if (event.canEdit || event.canRespondCancel) {
//     return (
//       <div
//         className="absolute top-0 right-0 z-[999]"
//         onPointerDown={(e) => {
//           // Stop propagation to prevent calendar event dragging
//           e.stopPropagation();
//         }}
//       >
//         <DotsMenu
//           onRemove={(e: React.MouseEvent<HTMLElement>) => {
//             getMenuEvent(onRemove)(e);
//           }}
//           onManage={(e: React.MouseEvent<HTMLElement>) => {
//             getMenuEvent(onManage)(e);
//           }}
//           triggerClassName="md:block block z-[999]"
//           canManage={event.canEdit}
//           canRemove={event.canRespondCancel}
//           canDuplicate={false}
//         />
//       </div>
//     );
//   }
//   return null;
// };
